import { useEffect, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { getAuthHeaders, API_BASE } from '../shared/api/base'

const HEARTBEAT_INTERVAL = 60_000 // 60 seconds

/**
 * Send presence heartbeats so the backend can suppress push notifications
 * when the user is actively using the app.
 *
 * On web: uses Page Visibility API (heartbeat only when tab is visible).
 * On native: uses Capacitor App state (heartbeat only when foregrounded).
 */
export function usePresenceHeartbeat(authenticated: boolean) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!authenticated) return

    async function sendHeartbeat() {
      try {
        const headers = await getAuthHeaders()
        // Fire-and-forget — don't use apiRequest to avoid caching/error handling overhead
        fetch(`${API_BASE}/push/heartbeat`, { method: 'POST', headers }).catch(() => {})
      } catch {
        // Not authenticated — skip silently
      }
    }

    function startHeartbeat() {
      if (intervalRef.current) return
      sendHeartbeat()
      intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)
    }

    function stopHeartbeat() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    if (Capacitor.isNativePlatform()) {
      // Native: heartbeat when app is foregrounded
      startHeartbeat()
      const listener = App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) startHeartbeat()
        else stopHeartbeat()
      })
      return () => {
        stopHeartbeat()
        listener.then(l => l.remove())
      }
    } else {
      // Web: heartbeat when tab is visible
      function handleVisibility() {
        if (document.visibilityState === 'visible') startHeartbeat()
        else stopHeartbeat()
      }
      handleVisibility()
      document.addEventListener('visibilitychange', handleVisibility)
      return () => {
        stopHeartbeat()
        document.removeEventListener('visibilitychange', handleVisibility)
      }
    }
  }, [authenticated])
}
